import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Download, Loader2, Upload } from 'lucide-react';
import {
  WorkdayCompareResult,
  WorkdayLogicPack,
  WorkdayPreviewMatch,
  WorkdayProposal,
  WorkdayReferenceRecord,
  WorkdayReferenceSessionSummary,
  compareResultsFromSession,
  compareWorkdaySimplified,
  downloadWorkdayFlaggedDocx,
  downloadWorkdayReferenceSourceDocx,
  getWorkdayReferenceSession,
  listWorkdayLogicPacks,
  listWorkdayReferenceSessions,
  previewWorkdayMatches,
} from './workdayReferenceApi';

const SOURCE_DOC_BYTES = 40 * 1024 * 1024;

/** Finished session SOP offered in the Workday Reference document picker. */
export interface WorkdaySessionSopOption {
  id: string;
  sourceFileName?: string | null;
  runAt?: string | null;
  sop: Record<string, unknown>;
}

interface WorkdayReferencePanelProps {
  processSessionId: number | null;
  sops: Array<WorkdaySessionSopOption | Record<string, unknown>>;
  onError: (message: string | null) => void;
}

interface UploadDraft {
  id: string;
  title: string;
  file: File;
}

interface SessionSopRow {
  id: string;
  title: string;
  sourceFileName: string;
  runAt: string | null;
  sop: Record<string, unknown>;
}

function sopTitleOf(sop: Record<string, unknown> | null | undefined): string {
  if (!sop) return '';
  const name = sop.processName || sop.title;
  return typeof name === 'string' ? name.trim() : '';
}

function isSessionSopOption(value: unknown): value is WorkdaySessionSopOption {
  return !!value
    && typeof value === 'object'
    && typeof (value as WorkdaySessionSopOption).id === 'string'
    && !!(value as WorkdaySessionSopOption).sop
    && typeof (value as WorkdaySessionSopOption).sop === 'object';
}

function normalizeSessionSop(
  input: WorkdaySessionSopOption | Record<string, unknown>,
  index: number,
): SessionSopRow | null {
  if (isSessionSopOption(input)) {
    const title = sopTitleOf(input.sop);
    if (!title) return null;
    return {
      id: input.id,
      title,
      sourceFileName: typeof input.sourceFileName === 'string' ? input.sourceFileName : '',
      runAt: typeof input.runAt === 'string' ? input.runAt : null,
      sop: input.sop,
    };
  }
  const title = sopTitleOf(input);
  if (!title) return null;
  return {
    id: `legacy-${index}-${title}`,
    title,
    sourceFileName: '',
    runAt: null,
    sop: input,
  };
}

function formatRunAt(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function requestError(err: any, fallback: string): string {
  const server = err?.response?.data?.error;
  if (typeof server === 'string' && server.trim()) return server;
  if (typeof err?.message === 'string' && err.message.trim() && !err.message.startsWith('Request failed')) {
    return err.message;
  }
  return fallback;
}

function storageKey(processSessionId: number | null): string {
  return `workday-reference-simplified:${processSessionId ?? 'none'}`;
}

function proposalKey(proposal: WorkdayProposal): string {
  return String(proposal.id || `${proposal.referenceQuote || ''}|${proposal.sopQuote || ''}`);
}

/** Prefer first selected row per SOP title — compare API keys jobs by title. */
function uniqueByTitle(rows: SessionSopRow[]): SessionSopRow[] {
  const seen = new Set<string>();
  return rows.filter((row) => {
    if (seen.has(row.title)) return false;
    seen.add(row.title);
    return true;
  });
}

export default function WorkdayReferencePanel({ processSessionId, sops, onError }: WorkdayReferencePanelProps) {
  const [packs, setPacks] = useState<WorkdayLogicPack[]>([]);
  const [logicPackId, setLogicPackId] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [uploads, setUploads] = useState<UploadDraft[]>([]);
  const [previews, setPreviews] = useState<WorkdayPreviewMatch[]>([]);
  const [confirmedProcesses, setConfirmedProcesses] = useState<Record<string, string>>({});
  const [session, setSession] = useState<WorkdayReferenceRecord | null>(null);
  const [results, setResults] = useState<WorkdayCompareResult[]>([]);
  const [approved, setApproved] = useState<Record<string, string[]>>({});
  const [loadingPacks, setLoadingPacks] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [pending, setPending] = useState<string | null>(null);
  const [compareError, setCompareError] = useState<string | null>(null);
  const [history, setHistory] = useState<WorkdayReferenceSessionSummary[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingSession, setLoadingSession] = useState(false);
  const [restoredFromHistory, setRestoredFromHistory] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const restoreAttempted = useRef(false);

  const sessionSops = useMemo(() => (
    sops
      .map((input, index) => normalizeSessionSop(input, index))
      .filter((row): row is SessionSopRow => !!row)
  ), [sops]);

  const selectedSessionRows = useMemo(
    () => uniqueByTitle(sessionSops.filter((row) => selectedIds.includes(row.id))),
    [sessionSops, selectedIds],
  );
  const selectedCount = selectedIds.length + uploads.length;
  const selectedPack = packs.find((pack) => pack.id === logicPackId) || null;
  const allConfirmed = previews.length > 0
    && previews.every((row) => !!confirmedProcesses[row.sopTitle]);
  const canPreview = !!logicPackId && selectedCount > 0 && !previewing && !comparing;
  const canCompare = !!logicPackId && allConfirmed && !comparing;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingPacks(true);
      try {
        const rows = await listWorkdayLogicPacks();
        if (cancelled) return;
        setPacks(rows);
        if (!logicPackId && rows.length) setLogicPackId(rows[0].id);
      } catch (err) {
        if (!cancelled) onError(requestError(err, 'The Workday logic sets could not be loaded.'));
      } finally {
        if (!cancelled) setLoadingPacks(false);
      }
    })();
    return () => { cancelled = true; };
  }, [onError]);

  const applyRestoredSession = (record: WorkdayReferenceRecord) => {
    const payload = record.payload || {};
    const restored = compareResultsFromSession(record);
    setSession(record);
    if (payload.logicPackId) setLogicPackId(String(payload.logicPackId));
    setConfirmedProcesses(
      payload.forcedProcesses && typeof payload.forcedProcesses === 'object'
        ? { ...payload.forcedProcesses }
        : {},
    );
    setResults(restored);
    setApproved({});
    setPreviews([]);
    setRestoredFromHistory(restored.length > 0);
    setCompareError(null);
    localStorage.setItem(storageKey(processSessionId), JSON.stringify({
      sessionId: record.id,
      logicPackId: payload.logicPackId || logicPackId,
    }));
  };

  const refreshHistory = async () => {
    setLoadingHistory(true);
    try {
      const rows = await listWorkdayReferenceSessions(30);
      setHistory(rows);
    } catch (err) {
      onError(requestError(err, 'Past Workday compares could not be loaded.'));
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadHistorySession = async (sessionId: number) => {
    if (!sessionId) return;
    setLoadingSession(true);
    onError(null);
    setCompareError(null);
    try {
      const record = await getWorkdayReferenceSession(sessionId);
      applyRestoredSession(record);
    } catch (err) {
      onError(requestError(err, 'That past compare could not be opened.'));
    } finally {
      setLoadingSession(false);
    }
  };

  useEffect(() => {
    void refreshHistory();
  }, []);

  useEffect(() => {
    if (restoreAttempted.current) return;
    restoreAttempted.current = true;
    let cancelled = false;
    (async () => {
      try {
        const raw = localStorage.getItem(storageKey(processSessionId));
        if (!raw) return;
        const parsed = JSON.parse(raw);
        const sessionId = Number(parsed?.sessionId);
        if (!Number.isFinite(sessionId) || sessionId <= 0) return;
        if (parsed?.logicPackId) setLogicPackId(String(parsed.logicPackId));
        setLoadingSession(true);
        const record = await getWorkdayReferenceSession(sessionId);
        if (cancelled) return;
        applyRestoredSession(record);
      } catch {
        // Stale local pointer — leave the tab ready for a new compare.
        localStorage.removeItem(storageKey(processSessionId));
      } finally {
        if (!cancelled) setLoadingSession(false);
      }
    })();
    return () => { cancelled = true; };
  }, [processSessionId]);

  const resetMatchState = () => {
    setPreviews([]);
    setConfirmedProcesses({});
    setResults([]);
    setApproved({});
    setRestoredFromHistory(false);
    setCompareError(null);
  };

  const startNewCompare = () => {
    resetMatchState();
    setSession(null);
    localStorage.removeItem(storageKey(processSessionId));
  };

  const toggleId = (id: string) => {
    setSelectedIds((prev) => (
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    ));
    resetMatchState();
  };

  const addUploads = (fileList: FileList | null) => {
    if (!fileList || !fileList.length) return;
    const next: UploadDraft[] = [];
    for (const file of Array.from(fileList)) {
      if (!/\.docx$/i.test(file.name)) {
        onError('Upload Word (.docx) files only.');
        continue;
      }
      if (file.size > SOURCE_DOC_BYTES) {
        onError('The file exceeds the upload size limit.');
        continue;
      }
      next.push({
        id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 7)}`,
        title: file.name.replace(/\.docx$/i, '').trim() || file.name,
        file,
      });
    }
    if (next.length) {
      setUploads((prev) => [...prev, ...next]);
      resetMatchState();
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  const removeUpload = (id: string) => {
    setUploads((prev) => prev.filter((row) => row.id !== id));
    resetMatchState();
  };

  const runPreview = async () => {
    if (!canPreview) return;
    setPreviewing(true);
    onError(null);
    try {
      const titles = [
        ...selectedSessionRows.map((row) => row.title),
        ...uploads.map((row) => row.title),
      ];
      const rows = await previewWorkdayMatches({ logicPackId, titles });
      setPreviews(rows);
      const nextConfirmed: Record<string, string> = {};
      for (const row of rows) {
        if (row.suggestedProcess && !row.ambiguous) {
          nextConfirmed[row.sopTitle] = row.suggestedProcess;
        }
      }
      setConfirmedProcesses(nextConfirmed);
      setResults([]);
      setApproved({});
    } catch (err) {
      onError(requestError(err, 'Process matches could not be previewed.'));
    } finally {
      setPreviewing(false);
    }
  };

  const runCompare = async () => {
    if (!canCompare) return;
    setComparing(true);
    onError(null);
    setCompareError(null);
    setResults([]);
    try {
      const response = await compareWorkdaySimplified({
        logicPackId,
        sessionId: session?.id || null,
        selectedTitles: selectedSessionRows.map((row) => row.title),
        sops: selectedSessionRows.map((row) => row.sop),
        uploads: uploads.map((row) => ({ title: row.title, file: row.file })),
        forcedProcesses: confirmedProcesses,
      });
      setSession(response.session);
      setResults(response.results);
      setApproved({});
      setRestoredFromHistory(false);
      localStorage.setItem(storageKey(processSessionId), JSON.stringify({
        sessionId: response.session.id,
        logicPackId,
      }));
      void refreshHistory();
    } catch (err) {
      const message = requestError(err, 'The documents could not be compared.');
      // Stale session from a prior compare used to 409 on re-upload; clear and retry once.
      const duplicate = /already stored on the session/i.test(message);
      if (duplicate && session?.id) {
        try {
          localStorage.removeItem(storageKey(processSessionId));
          setSession(null);
          const retry = await compareWorkdaySimplified({
            logicPackId,
            sessionId: null,
            selectedTitles: selectedSessionRows.map((row) => row.title),
            sops: selectedSessionRows.map((row) => row.sop),
            uploads: uploads.map((row) => ({ title: row.title, file: row.file })),
            forcedProcesses: confirmedProcesses,
          });
          setSession(retry.session);
          setResults(retry.results);
          setApproved({});
          setRestoredFromHistory(false);
          localStorage.setItem(storageKey(processSessionId), JSON.stringify({
            sessionId: retry.session.id,
            logicPackId,
          }));
          void refreshHistory();
          return;
        } catch (retryErr) {
          const retryMessage = requestError(retryErr, 'The documents could not be compared.');
          setCompareError(retryMessage);
          onError(retryMessage);
          return;
        }
      }
      setCompareError(message);
      onError(message);
    } finally {
      setComparing(false);
    }
  };

  const toggleApprove = (sopTitle: string, proposalId: string) => {
    setApproved((prev) => {
      const current = prev[sopTitle] || [];
      const next = current.includes(proposalId)
        ? current.filter((id) => id !== proposalId)
        : [...current, proposalId];
      return { ...prev, [sopTitle]: next };
    });
  };

  const approveAllFor = (sopTitle: string, proposalIds: string[]) => {
    setApproved((prev) => ({ ...prev, [sopTitle]: proposalIds }));
  };

  const allProposalIds = results.flatMap((row) => (
    (row.suggestions?.proposals || []).map((proposal) => proposalKey(proposal))
  ));
  const allApprovedIds = Object.values(approved).flat();
  const allApproved = allProposalIds.length > 0
    && allProposalIds.every((id) => allApprovedIds.includes(id))
    && results.every((row) => row.compared && !row.blocked);

  const downloadOriginal = async (sopTitle: string) => {
    if (!session?.id) return;
    setPending(`original:${sopTitle}`);
    try {
      await downloadWorkdayReferenceSourceDocx(session.id, sopTitle);
    } catch (err) {
      onError(requestError(err, 'The original Word file could not be downloaded.'));
    } finally {
      setPending(null);
    }
  };

  const downloadFlagged = async (sopTitle: string) => {
    if (!session?.id) return;
    const ids = approved[sopTitle] || [];
    if (!ids.length) {
      onError('Approve at least one discrepancy before downloading a flagged SOP.');
      return;
    }
    setPending(`flagged:${sopTitle}`);
    try {
      await downloadWorkdayFlaggedDocx(session.id, sopTitle, ids);
    } catch (err) {
      onError(requestError(err, 'The flagged Word file could not be downloaded.'));
    } finally {
      setPending(null);
    }
  };

  const downloadAllFlagged = async () => {
    for (const row of results) {
      if (!row.compared || row.blocked) continue;
      const ids = approved[row.sopTitle] || [];
      if (!ids.length) continue;
      await downloadFlagged(row.sopTitle);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Workday reference</h2>
        <p className="text-sm text-gray-600 mt-1">
          Choose SOP documents, pick a Workday logic set, confirm the matched process for each document, then compare.
          Past compares stay available after refresh.
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-md p-4 space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <label className="block text-sm font-medium text-gray-800 flex-1 min-w-[16rem]">
            Past compares
            <select
              value={session?.id || ''}
              onChange={(e) => {
                const id = Number(e.target.value);
                if (!id) {
                  startNewCompare();
                  return;
                }
                void loadHistorySession(id);
              }}
              disabled={loadingHistory || loadingSession || comparing}
              className="mt-1 w-full text-sm border border-gray-200 rounded-md px-2 py-1.5 font-normal"
            >
              <option value="">New compare</option>
              {history.map((row) => {
                const when = formatRunAt(row.comparedAt || row.updatedAt || row.createdAt);
                const label = row.title
                  || (row.titles && row.titles.length ? row.titles.join(', ') : `Session ${row.id}`);
                return (
                  <option key={row.id} value={row.id}>
                    {when ? `${label} · ${when}` : label}
                  </option>
                );
              })}
            </select>
          </label>
          <button
            type="button"
            onClick={startNewCompare}
            disabled={comparing || loadingSession}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-gray-700 border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
          >
            New compare
          </button>
        </div>
        {loadingSession ? (
          <p className="text-sm text-gray-500 inline-flex items-center gap-1.5">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
            Opening past compare…
          </p>
        ) : null}
        {restoredFromHistory && results.length > 0 ? (
          <p className="text-sm text-gray-600">
            Showing a saved compare. Download Original / Download New SOP still work.
            Start a new compare if you need to re-match documents.
          </p>
        ) : null}
      </div>

      <div className="bg-white border border-gray-200 rounded-md p-4 space-y-4">
        <label className="block text-sm font-medium text-gray-800">
          Workday logic set
          <select
            value={logicPackId}
            onChange={(e) => {
              setLogicPackId(e.target.value);
              resetMatchState();
            }}
            disabled={loadingPacks || comparing || previewing}
            className="mt-1 w-full max-w-xl text-sm border border-gray-200 rounded-md px-2 py-1.5 font-normal"
          >
            {loadingPacks ? <option value="">Loading…</option> : null}
            {!loadingPacks && !packs.length ? <option value="">No logic sets available</option> : null}
            {packs.map((pack) => (
              <option key={pack.id} value={pack.id}>
                {pack.label}
              </option>
            ))}
          </select>
        </label>
        {selectedPack?.processes?.length ? (
          <p className="text-xs text-gray-500">
            {selectedPack.processes.length} processes in this set
            {' · '}
            {selectedPack.processes.slice(0, 4).map((row) => row.workdayProcess.replace(/^Decision Summary:\s*/i, '').replace(/^BP:\s*/i, '')).join(', ')}
            {selectedPack.processes.length > 4 ? '…' : ''}
          </p>
        ) : null}

        <div>
          <div className="text-sm font-medium text-gray-800">Documents</div>
          <p className="text-xs text-gray-500 mt-1">
            Pick finished SOPs from Writing a new SOP and/or upload your own Word files ({selectedCount} selected).
          </p>

          {sessionSops.length > 0 ? (
            <ul className="mt-3 space-y-2">
              {sessionSops.map((row) => {
                const runLabel = formatRunAt(row.runAt);
                const metaParts = [
                  row.sourceFileName || null,
                  runLabel,
                  'From this session',
                ].filter(Boolean);
                return (
                  <li key={row.id}>
                    <label className="inline-flex items-start gap-2 text-sm text-gray-800">
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={selectedIds.includes(row.id)}
                        onChange={() => toggleId(row.id)}
                        disabled={comparing || previewing}
                      />
                      <span>
                        <span className="font-medium">{row.title}</span>
                        <span className="block text-xs text-gray-500">{metaParts.join(' · ')}</span>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="mt-3 text-sm text-gray-600">No finished SOP in this session yet. Upload a Word file below, or finish one on Writing a new SOP.</p>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={comparing || previewing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-white bg-electric-blue disabled:opacity-50"
            >
              <Upload className="w-4 h-4" />
              Upload Word SOP
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              multiple
              className="hidden"
              onChange={(e) => addUploads(e.target.files)}
            />
          </div>

          {uploads.length > 0 ? (
            <ul className="mt-3 space-y-2">
              {uploads.map((row) => (
                <li key={row.id} className="flex items-center justify-between gap-3 text-sm text-gray-800">
                  <span>
                    <span className="font-medium">{row.title}</span>
                    <span className="block text-xs text-gray-500">{row.file.name}</span>
                  </span>
                  <button
                    type="button"
                    className="text-electric-blue hover:underline"
                    onClick={() => removeUpload(row.id)}
                    disabled={comparing || previewing}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-1">
          <button
            type="button"
            onClick={runPreview}
            disabled={!canPreview}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-white bg-super-dark-purple disabled:opacity-50"
          >
            {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {previewing ? 'Matching…' : 'Match processes'}
          </button>
          {allApproved ? (
            <button
              type="button"
              onClick={downloadAllFlagged}
              disabled={!!pending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-white bg-emerald-700 disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              Download New SOP
            </button>
          ) : null}
        </div>
      </div>

      {previews.length > 0 ? (
        <div className="bg-white border border-gray-200 rounded-md p-4 space-y-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Confirm process matches</h3>
            <p className="text-sm text-gray-600 mt-1">
              Review the suggested Workday process for each document. Compare unlocks when every document has a process selected.
            </p>
          </div>
          {previews.map((preview) => {
            const choices = preview.choices.length ? preview.choices : preview.matches;
            const value = confirmedProcesses[preview.sopTitle] || '';
            return (
              <div key={preview.sopTitle} className="border border-gray-100 rounded-md p-3">
                <div className="text-sm font-medium text-gray-900">{preview.sopTitle}</div>
                {preview.message ? <p className="text-xs text-gray-500 mt-1">{preview.message}</p> : null}
                <div className="mt-2 flex flex-wrap items-end gap-3">
                  <label className="block text-sm font-medium text-gray-800 flex-1 min-w-[16rem] max-w-2xl">
                    Workday process
                    <select
                      value={value}
                      onChange={(e) => setConfirmedProcesses((prev) => ({
                        ...prev,
                        [preview.sopTitle]: e.target.value,
                      }))}
                      disabled={comparing}
                      className="mt-1 w-full text-sm border border-gray-200 rounded-md px-2 py-1.5 font-normal"
                    >
                      <option value="">Choose a process</option>
                      {choices.map((row) => (
                        <option key={row.workdayProcess} value={row.workdayProcess}>
                          {row.workdayProcess}
                        </option>
                      ))}
                    </select>
                  </label>
                  {previews.length === 1 ? (
                    <button
                      type="button"
                      onClick={runCompare}
                      disabled={!canCompare}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-white bg-electric-blue disabled:opacity-50"
                    >
                      {comparing ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      {comparing ? 'Comparing…' : 'Compare'}
                    </button>
                  ) : null}
                </div>
                {value && choices.find((row) => row.workdayProcess === value)?.summary ? (
                  <p className="text-xs text-gray-500 mt-2">
                    {choices.find((row) => row.workdayProcess === value)?.summary}
                  </p>
                ) : null}
              </div>
            );
          })}
          {previews.length > 1 ? (
            <div className="flex justify-end pt-1">
              <button
                type="button"
                onClick={runCompare}
                disabled={!canCompare}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-white bg-electric-blue disabled:opacity-50"
              >
                {comparing ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {comparing ? 'Comparing…' : 'Compare'}
              </button>
            </div>
          ) : null}
          {compareError ? (
            <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {compareError}
            </p>
          ) : null}
        </div>
      ) : null}

      {results.length > 0 ? (
        <div className="space-y-4">
          {results.map((result) => (
            <ResultCard
              key={result.sopTitle}
              result={result}
              confirmedProcess={confirmedProcesses[result.sopTitle] || ''}
              approvedIds={approved[result.sopTitle] || []}
              pending={pending}
              onApprove={(proposalId) => toggleApprove(result.sopTitle, proposalId)}
              onApproveAll={(ids) => approveAllFor(result.sopTitle, ids)}
              onDownloadOriginal={() => downloadOriginal(result.sopTitle)}
              onDownloadFlagged={() => downloadFlagged(result.sopTitle)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function ResultCard({
  result,
  confirmedProcess,
  approvedIds,
  pending,
  onApprove,
  onApproveAll,
  onDownloadOriginal,
  onDownloadFlagged,
}: {
  result: WorkdayCompareResult;
  confirmedProcess: string;
  approvedIds: string[];
  pending: string | null;
  onApprove: (proposalId: string) => void;
  onApproveAll: (ids: string[]) => void;
  onDownloadOriginal: () => void;
  onDownloadFlagged: () => void;
}) {
  // Holds are promoted into proposals server-side (flaggable for Download New SOP).
  const proposals = result.suggestions?.proposals || [];
  const notes = result.suggestions?.notes || [];
  const proposalIds = proposals.map(proposalKey);
  const allApproved = proposalIds.length > 0 && proposalIds.every((id) => approvedIds.includes(id));

  return (
    <div className="bg-white border border-gray-200 rounded-md p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900">{result.sopTitle}</h3>
          {confirmedProcess ? (
            <p className="text-xs text-gray-500 mt-1">Confirmed process: {confirmedProcess}</p>
          ) : null}
          {result.blocked ? (
            <p className="text-sm text-amber-800 mt-2">{result.message || 'Comparison blocked.'}</p>
          ) : (
            <p className="text-sm text-gray-600 mt-2">
              {proposals.length} potential discrepanc{proposals.length === 1 ? 'y' : 'ies'}
              {notes.length ? ` · ${notes.length} note${notes.length === 1 ? '' : 's'}` : ''}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={onDownloadOriginal}
            disabled={pending === `original:${result.sopTitle}` || result.kind === 'structured'}
            className="inline-flex items-center gap-1 text-sm font-medium text-electric-blue hover:underline disabled:opacity-50"
            title={result.kind === 'structured' ? 'Download Original is available for uploaded Word files' : undefined}
          >
            {pending === `original:${result.sopTitle}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            Download Original
          </button>
          <button
            type="button"
            onClick={onDownloadFlagged}
            disabled={!approvedIds.length || pending === `flagged:${result.sopTitle}`}
            className="inline-flex items-center gap-1 text-sm font-medium text-electric-blue hover:underline disabled:opacity-50"
          >
            {pending === `flagged:${result.sopTitle}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            Download New SOP
          </button>
        </div>
      </div>

      {!result.blocked && proposals.length > 0 ? (
        <div className="mt-4">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="text-sm font-medium text-gray-900">Potential discrepancies</div>
            <button
              type="button"
              onClick={() => onApproveAll(proposalIds)}
              className="text-xs font-medium text-electric-blue hover:underline"
            >
              {allApproved ? 'All approved' : 'Approve all'}
            </button>
          </div>
          <ul className="space-y-3">
            {proposals.map((proposal) => {
              const id = proposalKey(proposal);
              const checked = approvedIds.includes(id);
              return (
                <li key={id} className="border border-gray-100 rounded-md p-3">
                  <label className="flex items-start gap-2 text-sm text-gray-800">
                    <input type="checkbox" checked={checked} onChange={() => onApprove(id)} className="mt-1" />
                    <span>
                      <span className="font-medium text-gray-900">
                        {proposal.editClass || 'change'}
                        {checked ? <Check className="inline w-3.5 h-3.5 ml-1 text-emerald-600" /> : null}
                      </span>
                      {proposal.rationale ? (
                        <span className="block text-xs text-gray-600 mt-1">{proposal.rationale}</span>
                      ) : null}
                      {proposal.referenceQuote ? (
                        <span className="block text-xs text-gray-500 mt-1">Deck: {proposal.referenceQuote}</span>
                      ) : null}
                      {proposal.sopQuote ? (
                        <span className="block text-xs text-gray-500 mt-1">SOP: {proposal.sopQuote}</span>
                      ) : null}
                      {proposal.change_type !== 'flag' && proposal.proposed_content ? (
                        <span className="block text-xs text-gray-600 mt-1">Suggested: {proposal.proposed_content}</span>
                      ) : null}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {!result.blocked && !proposals.length ? (
        <p className="mt-3 text-sm text-gray-600">No potential discrepancies for this document. It may already align with the selected logic set, or the matched process had nothing to flag.</p>
      ) : null}
    </div>
  );
}
